import { BarChart, ChartsReferenceLine, ChartsTooltipContainer } from '@mui/x-charts';
import { useItemTooltip, useAxesTooltip } from '@mui/x-charts/ChartsTooltip';
import { toDate } from 'date-fns-tz';
import moment from 'moment-timezone';
import React, { useMemo } from 'react';
// Replace Google Charts with MUI X Charts
import { PersonVitalInsights, PersonVitalType, PersonVitalTimeFrame } from '@redux/person/personTypes';
import { CircledCustomIcon, TableIcon } from 'components/Icons';
import { Text } from 'components/Typography';
import { ChartProps } from 'containers/PatientDashboard/components/Charts/Charts';
import { createCustomRangeTooltip, getMinTime, getChartColor, getDateFormat } from 'utils/charts';
import { getDateFromStringIgnoreTimezone } from 'utils/time';
import { getFixedPointDisplay } from 'utils/util';
import style from './HeartBreathChart.scss';

type HeartBreathChartProps = Omit<ChartProps, 'isLoadingData'>;

const CHART_HEIGHT = 466;

export const HeartBreathChart: React.FC<HeartBreathChartProps> = ({
  timeZoneId,
  personVitalInsights,
  vitalType,
  timeFrame,
  selectedDate,
  minRate,
  maxRate,
  minControlRate,
  maxControlRate,
  onDaySelect,
  onChartDataAvailabilityChange
}) => {
  // Determine chart time range in patient's timezone
  const patTzMoment = moment.tz(selectedDate, timeZoneId);
  const isToday = patTzMoment.isSame(moment().tz(timeZoneId), 'day');
  let xMinMoment: moment.Moment;
  let xMaxMoment: moment.Moment;
  if (timeFrame === PersonVitalTimeFrame.TWENTY_FOUR_HOURS) {
    if (isToday) {
      xMaxMoment = moment().tz(timeZoneId);
    } else {
      xMaxMoment = patTzMoment.clone().startOf('day').add(1, 'day').hour(17).minute(0).second(0);
    }
    xMinMoment = xMaxMoment.clone().subtract(1, 'day');
  } else {
    xMaxMoment = patTzMoment.clone().startOf('day').add(1, 'day');
    xMinMoment = moment.tz(getMinTime(xMaxMoment.toDate(), timeFrame), timeZoneId);
  }
  const xMinValue = xMinMoment.toDate();
  const xMaxValue = xMaxMoment.toDate();
  const chartColor = getChartColor(vitalType);
  const title = vitalType === PersonVitalType.HEART_RATE ? 'Heart rate (bpm)' : 'Breathing rate (bpm)';

  const isDateValid = (dt: Date | null) => {
    return Boolean(dt && dt >= xMinValue && dt <= xMaxValue);
  };

  const isValueValid = (value: number | undefined) => {
    return typeof value === 'number' && !Number.isNaN(value) && value >= 0;
  };

  interface DataPoint {
    timestamp: Date;
    lower: number;
    diff: number;
    tooltipHtml: string;
    [key: string]: string | number | Date | null | undefined;
  }

  // MUI BarChart only renders x-axis labels for data within the chart, regardless of x-axis min/max
  // So we need to generate the x-axis labels manually by pushing the timestamps into the chartDataObjects
  const chartDataObjects: DataPoint[] = useMemo(() => {
    const aggregated = personVitalInsights?.listOfAggregatedProcessedVitalSigns ?? [];
    const rawMap = new Map<number, { minRaw: number; maxRaw: number; status?: any }>();
    aggregated.forEach((vitalSign) => {
      const minRaw = vitalType === PersonVitalType.HEART_RATE ? (vitalSign.minHeartRate ?? -1) : (vitalSign.minBreathingRate ?? -1);
      const maxRaw = vitalType === PersonVitalType.HEART_RATE ? (vitalSign.maxHeartRate ?? -1) : (vitalSign.maxBreathingRate ?? -1);
      const status = vitalType === PersonVitalType.HEART_RATE ? vitalSign.heartRateStatus : vitalSign.breathingRateStatus;
      // vitalSign.bucket is already in patient's timezone but because of the way BE structures the data, it is returned as if it were in UTC
      // Adjust the time to keep the hours/minutes but interpret it as the patient's timezone
      const timeZoneDateTimeString = vitalSign.bucket?.endsWith('Z') ? vitalSign.bucket?.substring(0, vitalSign.bucket?.length - 1) : vitalSign.bucket;
      let start = toDate(timeZoneDateTimeString, { timeZone: timeZoneId });
      if (!start) return;
      let end = moment(start).add(30, 'minutes').toDate();
      if (timeFrame === PersonVitalTimeFrame.TWELVE_MONTHS) {
        start = moment.tz(start, timeZoneId).startOf('month').toDate();
        end = moment.tz(start, timeZoneId).add(30, 'minutes').toDate();
      } else if (timeFrame === PersonVitalTimeFrame.SEVEN_DAYS || timeFrame === PersonVitalTimeFrame.THIRTY_DAYS) {
        // For 7-day and 30-day timeframes, bucket is a day, so adjust start and end to cover the whole day
        start = moment.tz(start, timeZoneId).startOf('day').toDate();
        end = moment.tz(start, timeZoneId).add(30, 'minutes').toDate();
      }
      if (!isValueValid(minRaw) || !isValueValid(maxRaw) || !isDateValid(start) || !isDateValid(end)) return;
      const mapKey = timeFrame === PersonVitalTimeFrame.TWENTY_FOUR_HOURS
        ? moment.tz(start, timeZoneId).valueOf()
        : moment.tz(start, timeZoneId).startOf('day').valueOf();

      const existing = rawMap.get(mapKey);
      if (existing) {
        // Aggregate: take min of mins and max of maxes
        rawMap.set(mapKey, {
          minRaw: Math.min(existing.minRaw, minRaw),
          maxRaw: Math.max(existing.maxRaw, maxRaw),
          status: existing.status || status
        });
      } else {
        rawMap.set(mapKey, { minRaw, maxRaw, status });
      }
    });
    // if no real data, do not render empty chart, trigger noData view
    if (rawMap.size === 0) {
      return [];
    }
    const steps: Date[] = [];
    // Generate x-axis steps based on patient's timezone
    const startMoment = moment.tz(xMinValue, timeZoneId);
    const endMoment = moment.tz(xMaxValue, timeZoneId);
    switch (timeFrame) {
      case PersonVitalTimeFrame.TWENTY_FOUR_HOURS: {
        const stepMinutes = 30;
        let bucketStart = startMoment.clone();
        const remainder = bucketStart.minute() % stepMinutes;
        if (remainder !== 0) bucketStart = bucketStart.add(stepMinutes - remainder, 'minutes');
        bucketStart = bucketStart.seconds(0).milliseconds(0);
        let current = bucketStart.clone();
        while (current <= endMoment) {
          steps.push(current.toDate());
          current = current.add(stepMinutes, 'minutes');
        }
        break;
      }
      case PersonVitalTimeFrame.SEVEN_DAYS:
      case PersonVitalTimeFrame.THIRTY_DAYS: {
        const daysCount = timeFrame === PersonVitalTimeFrame.SEVEN_DAYS ? 7 : 30;
        const dayStart = patTzMoment
          .clone()
          .startOf('day')
          .subtract(daysCount - 1, 'days');
        let current = dayStart.clone();
        for (let i = 0; i < daysCount; i++) {
          steps.push(current.toDate());
          current = current.add(1, 'day');
        }
        break;
      }
      case PersonVitalTimeFrame.TWELVE_MONTHS: {
        const monthsCount = 12;
        const monthStart = moment
          .tz(xMaxValue, timeZoneId)
          .startOf('month')
          .subtract(monthsCount - 1, 'months');
        let current = monthStart.clone();
        for (let i = 0; i < monthsCount; i++) {
          steps.push(current.toDate());
          current = current.add(1, 'month');
        }
        break;
      }
      default:
        break;
    }
    return steps.map((timestamp) => {
      const bucket = rawMap.get(timestamp.getTime());
      let lower: number;
      let rawDiff: number;
      let status: any;
      if (bucket) {
        lower = bucket.minRaw;
        rawDiff = bucket.maxRaw - bucket.minRaw;
        status = bucket.status;
      } else {
        lower = 0;
        rawDiff = 0;
        status = undefined;
      }
      const diff = bucket && rawDiff === 0 ? 1 : rawDiff;
      let end: Date;
      switch (timeFrame) {
        case PersonVitalTimeFrame.TWENTY_FOUR_HOURS:
          end = moment(timestamp).add(30, 'minutes').toDate();
          break;
        case PersonVitalTimeFrame.SEVEN_DAYS:
        case PersonVitalTimeFrame.THIRTY_DAYS:
          end = moment(timestamp).add(1, 'day').toDate();
          break;
        case PersonVitalTimeFrame.TWELVE_MONTHS:
          end = moment(timestamp).add(1, 'month').toDate();
          break;
        default:
          end = moment(timestamp).add(30, 'minutes').toDate();
      }
      const tooltipHtml = createCustomRangeTooltip(
        timestamp,
        end,
        getFixedPointDisplay(lower),
        getFixedPointDisplay(bucket ? bucket.minRaw + rawDiff : 0),
        timeFrame,
        status,
        timeZoneId
      );
      return { timestamp, lower, diff, tooltipHtml };
    });
  }, [personVitalInsights, vitalType, timeFrame, xMinValue, xMaxValue]);

  // Notify parent about data availability
  React.useEffect(() => {
    onChartDataAvailabilityChange?.(chartDataObjects.length > 0);
  }, [chartDataObjects.length, onChartDataAvailabilityChange]);

  const HeartBreathChartTooltip: React.FC = () => {
    const tooltipData = useItemTooltip();
    if (!tooltipData || tooltipData.identifier.dataIndex == null) {
      return null;
    }
    const idx = tooltipData.identifier.dataIndex;
    const dp = chartDataObjects[idx];
    return <div dangerouslySetInnerHTML={{ __html: dp.tooltipHtml }} />;
  };

  // Render chart title visibly using Text, and remove default SVG margins on left/bottom
  return chartDataObjects.length > 0 ? (
    <div className={style.chartWrapper}>
      <BarChart
        className={style.chartContainer}
        title={title}
        margin={{ left: 0, bottom: 0 }}
        // Use dataset and stacked bars to represent range [lower, lower + diff]
        dataset={chartDataObjects}
        series={[
          { id: 'lower', type: 'bar', dataKey: 'lower', stack: 'range', color: 'transparent' },
          { id: 'range', type: 'bar', dataKey: 'diff', stack: 'range', color: chartColor }
        ]}
        onItemClick={(_e, item) => {
          if ((timeFrame === PersonVitalTimeFrame.SEVEN_DAYS || timeFrame === PersonVitalTimeFrame.THIRTY_DAYS) && item.dataIndex != null) {
            const day = moment.tz(chartDataObjects[item.dataIndex].timestamp, timeZoneId).startOf('day').toDate();
            onDaySelect?.(day);
          }
        }}
        xAxis={[
          {
            id: 'xAxis',
            data: chartDataObjects.map((d) => d.timestamp),
            min: xMinValue,
            max: xMaxValue,
            scaleType: 'band',
            disableTicks: true,
            // show labels every 2 hours for 24h timeframe, and every other day for 30-day timeframe
            tickInterval:
              timeFrame === PersonVitalTimeFrame.TWELVE_MONTHS
                ? (_value, index) => true
                : timeFrame === PersonVitalTimeFrame.TWENTY_FOUR_HOURS
                  ? (_value, index) => index % 4 === 0
                  : timeFrame === PersonVitalTimeFrame.THIRTY_DAYS
                    ? (_value, index) => index % 2 === 0
                    : undefined,
            valueFormatter: (value, context) =>
              moment(value as Date)
                .tz(timeZoneId)
                .format(getDateFormat(timeFrame) ?? '')
          }
        ]}
        yAxis={[
          {
            id: 'yAxis',
            min: vitalType === PersonVitalType.HEART_RATE ? 30 : 0,
            max: vitalType === PersonVitalType.HEART_RATE ? 130 : 50,
            tickInterval: (() => {
              const baseTicks = vitalType === PersonVitalType.HEART_RATE ? [30, 50, 70, 90, 110, 130] : [0, 10, 20, 30, 40, 50];
              const controlTicks = [minControlRate, maxControlRate].filter((v): v is number => v != null && !Number.isNaN(v));
              return [...baseTicks, ...controlTicks];
            })(),
            fill: chartColor,
            tickLabelStyle: { fill: chartColor }
          }
        ]}
        height={CHART_HEIGHT}
        grid={{ horizontal: true }}
        slotProps={{
          tooltip: { trigger: 'none' }
        }}
      >
        <ChartsTooltipContainer trigger="item">
          <HeartBreathChartTooltip />
        </ChartsTooltipContainer>
        {minControlRate != null && <ChartsReferenceLine y={minControlRate} lineStyle={{ stroke: chartColor, strokeDasharray: '4 4' }} />}
        {maxControlRate != null && <ChartsReferenceLine y={maxControlRate} lineStyle={{ stroke: chartColor, strokeDasharray: '4 4' }} />}
      </BarChart>
      {timeFrame === PersonVitalTimeFrame.TWENTY_FOUR_HOURS && !isToday && (
        <div className={style.dateAxis}>
          <div>{moment.tz(selectedDate, timeZoneId).format('MMM D')}</div>
          <div>{moment.tz(selectedDate, timeZoneId).add(1, 'day').format('MMM D')}</div>
        </div>
      )}
    </div>
  ) : (
    <div className={style.noDataChartContainer}>
      <CircledCustomIcon icon={TableIcon} />
      <Text>No data to display</Text>
    </div>
  );
};
